import { inject, Injector, runInInjectionContext } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import type { ActivatedRouteSnapshot, CanActivateFn, Data, UrlTree } from '@angular/router';
import { NavigationService } from '@core/navigation/services/navigation.service';
import { AppPathsMap } from '@core/routing/app-paths';
import { signalsToObservable } from '@core/utils/signal.utilities';
import type { ResponseUserGrantDto } from '@kerberos-compliance/compliance-shared-ts/core/iam/authorization/dtos/response.user-grant.dto';
import type { NamedRole } from '@kerberos-compliance/compliance-shared-ts/core/iam/authorization/enums/named-role';
import type { Observable } from 'rxjs';
import { filter, map, switchMap, take } from 'rxjs/operators';
import { AuthStore } from '@/auth/store/auth.store';
import type { GrantLoadingState } from '@/auth/types/auth.types';
import { GrantLoadingStateMap } from '@/auth/types/auth.types';

type AccessDecisionContext = {
  grantLoadingState: GrantLoadingState;
  ownGrant: ResponseUserGrantDto | undefined;
  requiredRoles: NamedRole[];
  enableRedirect: boolean;
};

/**
 * Maps grant loading error states to their corresponding error routes.
 */
const grantStateErrorRoutes: Partial<Record<GrantLoadingState, string>> = {
  [GrantLoadingStateMap.Error]: AppPathsMap.Error.GrantLoadError,
  [GrantLoadingStateMap.Empty]: AppPathsMap.Error.NoGrants,
};

/**
 * Constructs a URL tree for the landing page route.
 *
 * @param {NavigationService} navigationService - The navigation service used to create the URL tree.
 * @returns {UrlTree} A URL tree object pointing to the landing page route.
 */
const createLandingUrlTree = (navigationService: NavigationService): UrlTree => {
  return navigationService.createUrlTree([AppPathsMap.Home]);
};

/**
 * Determines whether a given state is associated with a grant loading error.
 *
 * @param {GrantLoadingState} state - The current state of grant loading.
 * @returns {boolean} `true` if the specified state indicates a loading error; otherwise, `false`.
 */
const isGrantLoadingError = (state: GrantLoadingState): boolean => {
  return Object.hasOwn(grantStateErrorRoutes, state);
};

/**
 * Handles access denial logic within the application by conditionally triggering a redirection
 * to a specified error route.
 *
 * @param {NavigationService} navigationService - The navigation service used for navigation.
 * @param {string} errorRoute - The route to navigate to in case of access denial.
 * @param {boolean} enableRedirect - Determines if redirection should be performed upon access denial.
 * @returns {false} Always returns false to indicate the operation's outcome.
 */
const handleAccessDenied = (
  navigationService: NavigationService,
  errorRoute: string,
  enableRedirect: boolean,
): false => {
  if (enableRedirect) {
    void navigationService.navigateTo([errorRoute]);
  }
  return false;
};

/**
 * Resolves access decisions based on user grants, roles, and loading states.
 *
 * This function determines whether a user should be granted access to a specific route
 * or redirected elsewhere, based on their access context and role requirements.
 *
 * @param {NavigationService} navigationService
 * The navigation service used for navigation and redirection.
 * @param {AccessDecisionContext} context
 * The access decision context containing information about the user's grant,
 * required roles for the route, and redirect settings.
 *        - `grantLoadingState` (string): The state of the grant's loading process,
 *           which could indicate errors or valid grants.
 *        - `ownGrant` (object | null): The user's current access grant, primarily
 *           used to verify permissions via their role.
 *        - `requiredRoles` (string[]): An array of roles required to access the route.
 *        - `enableRedirect` (boolean): Flags whether the user should be redirected
 *           upon denied access or if a direct boolean response suffices.
 * @returns {boolean | UrlTree}
 * A boolean indicating access (true for granted, false for denied)
 * or a `UrlTree` to redirect users to an appropriate route based on the access state.
 *
 * The access decision process includes:
 * - Verifying the loading state of the user's grant. If the grant loading failed,
 *   the user is redirected to an error or unauthorized route.
 * - Checking whether the user has a valid grant. If none exists, access is denied.
 * - Enforcing redirection if `enableRedirect` is true, sending the user to a landing page.
 * - Assessing the user's role against the `requiredRoles` list to determine if access is granted.
 * - Redirecting users with invalid roles but a valid grant to a landing page.
 */
const resolveAccessDecision = (
  navigationService: NavigationService,
  context: AccessDecisionContext,
): boolean | UrlTree => {
  const { grantLoadingState, ownGrant, requiredRoles, enableRedirect } = context;
  const errorRoute = grantStateErrorRoutes[grantLoadingState];
  if (errorRoute && isGrantLoadingError(grantLoadingState)) {
    return handleAccessDenied(navigationService, errorRoute, enableRedirect);
  }
  if (!ownGrant) {
    return handleAccessDenied(navigationService, AppPathsMap.Error.Unauthorized, enableRedirect);
  }
  if (enableRedirect) {
    return createLandingUrlTree(navigationService);
  }
  return requiredRoles.includes(ownGrant.roleDisplayName) ? true : createLandingUrlTree(navigationService);
};

export type RouteDataWithRoles = Data & { roles: NamedRole[] };

/**
 * Guards routes based on user roles.
 *
 * This function checks if the current user has the required roles defined in the
 * route's data. If the user has the necessary role, they are granted access to the
 * route. Otherwise, they are redirected to an appropriate error page.
 *
 * @param {ActivatedRouteSnapshot} route - The route snapshot containing route-specific data.
 * @returns {Observable<boolean | UrlTree>} - An observable that emits whether the user can activate the route.
 */
export const roleGuard: CanActivateFn = (route: ActivatedRouteSnapshot): Observable<boolean | UrlTree> => {
  const injector = inject(Injector);
  const authStore = injector.get(AuthStore);
  const navigationService = injector.get(NavigationService);

  const requiredRoles: NamedRole[] = (route.data as RouteDataWithRoles).roles;
  const isEnableRedirect: boolean = Boolean(route.data['enableRedirect']);

  const sessionState$ = signalsToObservable(injector, {
    isUserLoggedIn: authStore.isUserLoggedIn,
    grantLoadingComplete: authStore.grantLoadingComplete,
    grantLoadingState: authStore.grantLoadingState,
  });

  const authTokensAndOwnGrant$ = runInInjectionContext(injector, () =>
    toObservable(authStore.selectAuthTokensAndOwnGrant(undefined, requiredRoles)),
  );

  return sessionState$.pipe(
    filter(({ isUserLoggedIn, grantLoadingComplete }) => isUserLoggedIn && grantLoadingComplete),
    take(1),
    switchMap(({ grantLoadingState }) =>
      authTokensAndOwnGrant$.pipe(
        take(1),
        map(({ ownGrant }) =>
          resolveAccessDecision(navigationService, {
            grantLoadingState,
            ownGrant,
            requiredRoles,
            enableRedirect: isEnableRedirect,
          }),
        ),
      ),
    ),
  );
};
